// iOS / mobile direct ONNX Runtime Web path for RMBG-1.4.
//
// This module is intentionally simple and modelled closely on the working
// reference architecture from bg-remover-sandy.vercel.app, which is known to
// run RMBG-1.4 on iPhone Safari. We DO NOT use @huggingface/transformers
// here — that path is the suspected failure point on iOS (audit:
// docs/ai-audits/2026-04-24-onnx-fix-audit.md).
//
// Constraints honoured:
//   - Same-origin model: the 44MB model_quantized.onnx is split into ~10MB
//     chunks under public/models/ at build time (scripts/download-mobile-model.mjs)
//     to stay within Cloudflare Pages' 25MB per-file limit. At runtime we
//     fetch /models/model_quantized.manifest.json, then fetch each chunk and
//     reassemble them into one ArrayBuffer. NO huggingface.co fetches at
//     runtime — COEP: require-corp would block them on Safari.
//   - Same-origin ORT WASM: /ort/. The host (lib/process.ts) sets
//     env.wasm.wasmPaths = "/ort/" via onnxruntime-web's shared env, which
//     onnxruntime-web also reads.
//   - No header changes (COOP / COEP / CORP / wrangler).
//
// Diagnostic shape mirrors lib/process.ts so error reports built from
// either path look identical to the UI / Copy-diagnostics path.
//
// IMPORTANT: Do not statically import onnxruntime-web in this module. Vite's
// bundler rewrites WASM backend resolution, which broke iOS session creation
// in production. The working reference architecture loads ORT as a script, so
// this module injects /ort/ort.min.js and then uses window.ort.

type OrtTensorData = Float32Array | Uint8Array | Int8Array | number[] | ArrayLike<number>;

interface OrtTensor {
  data: OrtTensorData;
}

interface OrtInferenceSession {
  inputNames: string[];
  outputNames: string[];
  run(feeds: Record<string, OrtTensor>): Promise<Record<string, OrtTensor>>;
  release?: () => Promise<void> | void;
}

interface OrtNamespace {
  env: {
    wasm?: {
      wasmPaths?: string;
      proxy?: boolean;
      numThreads?: number;
    };
  };
  InferenceSession: {
    create(
      model: ArrayBuffer,
      options: { executionProviders: string[]; graphOptimizationLevel: string },
    ): Promise<OrtInferenceSession>;
  };
  Tensor: new (type: string, data: Float32Array, dims: number[]) => OrtTensor;
}

declare global {
  interface Window {
    ort?: OrtNamespace;
  }
}

// ---------- Public types (compatible with lib/process.ts) ----------

export interface MobilePhaseRecord {
  phase: string;
  status: "start" | "end" | "error" | "timeout";
  t: string;
  elapsedMs?: number;
  context?: Record<string, unknown>;
}

export interface MobileRuntimeImageInfo {
  fileName?: string;
  fileType?: string;
  fileSize?: number;
  width?: number;
  height?: number;
}

export interface MobileRuntimeDiagnosticDetails {
  failedPhase: string;
  elapsedMs: number;
  lastCompletedStep: string;
  modelSelected: string;
  isIOS: boolean;
  userAgent: string;
  crossOriginIsolated: boolean;
  sharedArrayBufferType: string;
  image?: MobileRuntimeImageInfo;
  phaseHistory: MobilePhaseRecord[];
  wasmProxy?: boolean;
  wasmPaths?: string;
  message: string;
}

export class MobileRuntimePhaseError extends Error {
  details: MobileRuntimeDiagnosticDetails;
  constructor(message: string, details: MobileRuntimeDiagnosticDetails, cause?: unknown) {
    super(message, { cause });
    this.name = "RuntimePhaseError"; // matches host name so getRuntimeErrorDetails recognises it
    this.details = details;
  }
}

// Diagnostics injection — host (lib/process.ts) provides closures so the
// mobile module's phases land in the same lastRuntimeInfo / phaseHistory the
// rest of the app already reads. Optional: defaults are no-ops.
export interface MobileDiagnosticsHost {
  startPhase: (phase: string, context?: Record<string, unknown>) => number;
  endPhase: (phase: string, startedAt: number, context?: Record<string, unknown>) => void;
  recordError: (
    phase: string,
    message: string,
    startedAt: number,
    context: Record<string, unknown> | undefined,
    cause: unknown,
    status: "error" | "timeout",
  ) => MobileRuntimeDiagnosticDetails;
  setLastCompletedStep: (step: string) => void;
}

const noopDiagnostics: MobileDiagnosticsHost = {
  startPhase: () => (typeof performance !== "undefined" ? performance.now() : Date.now()),
  endPhase: () => {},
  recordError: (phase, message) => ({
    failedPhase: phase,
    elapsedMs: 0,
    lastCompletedStep: "unknown",
    modelSelected: "briaai/RMBG-1.4 (mobile-ort)",
    isIOS: true,
    userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "",
    crossOriginIsolated:
      typeof globalThis.crossOriginIsolated === "boolean" ? globalThis.crossOriginIsolated : false,
    sharedArrayBufferType: typeof SharedArrayBuffer,
    phaseHistory: [],
    message,
  }),
  setLastCompletedStep: () => {},
};

// ---------- Constants ----------

// Path constraint: same-origin only. Public/models/ is shipped from our build.
// The model is split into chunks (each <25 MB) to fit Cloudflare Pages' file-
// size limit. A manifest at MANIFEST_URL lists the chunk filenames and sizes.
// At runtime we fetch the manifest, fetch all chunks, and concatenate them
// into a single ArrayBuffer before passing to InferenceSession.create().
const MANIFEST_URL = "/models/model_quantized.manifest.json";
const MODEL_BASE_URL = "/models/";
const MODEL_INPUT_SIZE = 1024;
const MODEL_ID_FOR_REPORTING = "briaai/RMBG-1.4 (mobile-ort, quantized)";

// Phase timeout budgets — mirror lib/process.ts so UX failure modes match.
const ORT_RUNTIME_LOAD_TIMEOUT_MS = 15000;
const MODEL_FETCH_TIMEOUT_MS = 120000;
const SESSION_CREATE_TIMEOUT_MS = 60000;
const IMAGE_DECODE_TIMEOUT_MS = 15000;
const PREPROCESS_TIMEOUT_MS = 30000;
const INFERENCE_TIMEOUT_MS = 60000;
const POSTPROCESS_TIMEOUT_MS = 30000;
const CANVAS_BLOB_TIMEOUT_MS = 20000;

// ---------- Module state ----------

interface MobileState {
  ort: OrtNamespace | null;
  session: OrtInferenceSession | null;
  inputName: string;
  outputName: string;
  modelBytes: number;
}

const mobileState: MobileState = {
  ort: null,
  session: null,
  inputName: "input",
  outputName: "output",
  modelBytes: 0,
};

let activeDiagnostics: MobileDiagnosticsHost = noopDiagnostics;

export function setMobileDiagnosticsHost(host: MobileDiagnosticsHost): void {
  activeDiagnostics = host;
}

// ---------- Phase wrapper ----------

async function withMobilePhase<T>(
  phase: string,
  timeoutMs: number,
  action: () => Promise<T>,
  context?: Record<string, unknown>,
): Promise<T> {
  const startedAt = activeDiagnostics.startPhase(phase, { ...context, timeoutMs });
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  const timeoutPromise = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(() => {
      const details = activeDiagnostics.recordError(
        phase,
        `${phase} timed out after ${timeoutMs}ms`,
        startedAt,
        { ...context, timeoutMs },
        undefined,
        "timeout",
      );
      reject(new MobileRuntimePhaseError(`${phase} timed out after ${timeoutMs}ms`, details));
    }, timeoutMs);
  });

  try {
    const result = await Promise.race([action(), timeoutPromise]);
    activeDiagnostics.endPhase(phase, startedAt, { ...context, timeoutMs });
    return result;
  } catch (err) {
    if (err instanceof MobileRuntimePhaseError) throw err;
    if (err instanceof Error && err.name === "RuntimePhaseError") throw err;
    const message = err instanceof Error ? err.message : String(err);
    const details = activeDiagnostics.recordError(
      phase,
      `${phase} failed: ${message}`,
      startedAt,
      { ...context, timeoutMs },
      err,
      "error",
    );
    throw new MobileRuntimePhaseError(`${phase} failed: ${message}`, details, err);
  } finally {
    if (timeoutId !== undefined) clearTimeout(timeoutId);
  }
}

// ---------- ORT runtime script loading ----------

let ortLoadPromise: Promise<OrtNamespace> | null = null;

function getWindowOrt(): OrtNamespace | undefined {
  return typeof window !== "undefined" ? window.ort : undefined;
}

async function loadOrtRuntime(): Promise<OrtNamespace> {
  const existing = getWindowOrt();
  if (existing) return existing;
  if (ortLoadPromise) return ortLoadPromise;

  ortLoadPromise = new Promise<OrtNamespace>((resolve, reject) => {
    if (typeof document === "undefined") {
      reject(new Error("Cannot load ORT runtime outside a browser document"));
      return;
    }

    // Avoid waiting forever on a stale script tag whose load event already
    // fired before window.ort was observed. Reinsert a fresh same-origin script.
    const stale = document.querySelector<HTMLScriptElement>('script[src="/ort/ort.min.js"]');
    stale?.remove();

    const script = document.createElement("script");
    script.src = "/ort/ort.min.js";
    script.async = true;
    script.onload = () => {
      const ortRuntime = getWindowOrt();
      if (!ortRuntime) {
        reject(new Error("/ort/ort.min.js loaded but window.ort is missing"));
        return;
      }
      resolve(ortRuntime);
    };
    script.onerror = () => {
      reject(new Error("Failed to load /ort/ort.min.js"));
    };
    document.head.appendChild(script);
  });

  return ortLoadPromise;
}

// ---------- Model manifest + chunked fetch ----------

interface ModelManifest {
  originalFile: string;
  totalBytes: number;
  chunkSize: number;
  chunks: { file: string; bytes: number }[];
}

async function fetchManifest(): Promise<ModelManifest> {
  const response = await fetch(MANIFEST_URL, { credentials: "same-origin" });
  if (!response.ok) {
    throw new Error(
      `Manifest fetch failed: HTTP ${response.status} ${response.statusText} for ${MANIFEST_URL}`,
    );
  }
  const manifest: ModelManifest = await response.json();
  if (!manifest.chunks?.length || !manifest.totalBytes) {
    throw new Error("Invalid manifest: missing chunks or totalBytes");
  }
  return manifest;
}

async function fetchChunkedModel(manifest: ModelManifest): Promise<ArrayBuffer> {
  const totalBytes = manifest.totalBytes;
  const merged = new Uint8Array(totalBytes);
  let offset = 0;

  for (let i = 0; i < manifest.chunks.length; i++) {
    const chunk = manifest.chunks[i];
    const url = `${MODEL_BASE_URL}${chunk.file}`;
    console.log(
      `[mobile-ort] fetching chunk ${i + 1}/${manifest.chunks.length}: ${chunk.file} (${chunk.bytes} bytes)`,
    );

    const response = await fetch(url, { credentials: "same-origin" });
    if (!response.ok) {
      throw new Error(
        `Chunk fetch failed: HTTP ${response.status} ${response.statusText} for ${url}`,
      );
    }

    const buf = new Uint8Array(await response.arrayBuffer());
    if (buf.byteLength !== chunk.bytes) {
      throw new Error(
        `Chunk size mismatch for ${chunk.file}: expected ${chunk.bytes}, got ${buf.byteLength}`,
      );
    }
    merged.set(buf, offset);
    offset += buf.byteLength;

    const pct = Math.round((offset / totalBytes) * 100);
    console.log(`[mobile-ort] model download progress: ${pct}% (${offset}/${totalBytes})`);
  }

  if (offset !== totalBytes) {
    throw new Error(
      `Total reassembled size mismatch: expected ${totalBytes}, got ${offset}`,
    );
  }

  mobileState.modelBytes = totalBytes;
  return merged.buffer;
}

// ---------- Initialization ----------

export async function initializeMobileOrtModel(): Promise<boolean> {
  if (mobileState.session) {
    return true;
  }

  activeDiagnostics.setLastCompletedStep("mobile-ort:initialize:start");

  const ortRuntime = await withMobilePhase(
    "ort runtime script load",
    ORT_RUNTIME_LOAD_TIMEOUT_MS,
    () => loadOrtRuntime(),
    { modelId: MODEL_ID_FOR_REPORTING, path: "iOS-direct-ort", source: "/ort/ort.min.js" },
  );

  // Configure window.ort before session creation. Keep the iOS direct-ORT
  // baseline deliberately simple: same-origin WASM, no proxy worker, one
  // thread. This mirrors the working script-tag reference and avoids Vite's
  // bundled WASM resolution path entirely.
  if (!ortRuntime.env.wasm) {
    throw new Error("window.ort.env.wasm is unavailable after loading /ort/ort.min.js");
  }
  ortRuntime.env.wasm.wasmPaths = "/ort/";
  ortRuntime.env.wasm.proxy = false;
  ortRuntime.env.wasm.numThreads = 1;
  mobileState.ort = ortRuntime;

  const modelBuffer = await withMobilePhase(
    "model config/model fetch + model load",
    MODEL_FETCH_TIMEOUT_MS,
    async () => {
      const manifest = await fetchManifest();
      console.log(
        `[mobile-ort] manifest loaded: ${manifest.chunks.length} chunks, ${manifest.totalBytes} bytes total`,
      );
      return fetchChunkedModel(manifest);
    },
    { modelId: MODEL_ID_FOR_REPORTING, path: "iOS-direct-ort", source: MANIFEST_URL },
  );

  const session = await withMobilePhase(
    "ort session create",
    SESSION_CREATE_TIMEOUT_MS,
    () =>
      ortRuntime.InferenceSession.create(modelBuffer, {
        executionProviders: ["wasm"],
        graphOptimizationLevel: "all",
      }),
    { modelId: MODEL_ID_FOR_REPORTING, modelBytes: mobileState.modelBytes },
  );

  // Resolve actual input/output names from the session metadata. RMBG-1.4
  // typically exposes input "input" and output "output", which matches the
  // bg-remover-sandy reference, but we read whatever the model declares
  // rather than hard-coding.
  const inputNames = session.inputNames;
  const outputNames = session.outputNames;
  if (!inputNames?.length || !outputNames?.length) {
    throw new Error("ORT session exposes no input/output names — model file may be corrupt");
  }
  mobileState.inputName = inputNames[0];
  mobileState.outputName = outputNames[0];
  mobileState.session = session;

  console.log(
    `[mobile-ort] session ready — input="${mobileState.inputName}", output="${mobileState.outputName}", model=${mobileState.modelBytes} bytes`,
  );
  activeDiagnostics.setLastCompletedStep("mobile-ort:initialize:complete");
  return true;
}

// ---------- Image utilities ----------

function decodeImageElement(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = (e) => {
      URL.revokeObjectURL(url);
      reject(new Error(`Image decode failed: ${typeof e === "string" ? e : "load error"}`));
    };
    img.src = url;
  });
}

function imageToInputCanvas(
  img: HTMLImageElement,
  size: number,
): { resized: HTMLCanvasElement; original: HTMLCanvasElement } {
  // Original-resolution canvas — used for the final composite so the output
  // matches the input dimensions, not the model's 1024 work size.
  const original = document.createElement("canvas");
  original.width = img.naturalWidth;
  original.height = img.naturalHeight;
  const origCtx = original.getContext("2d", { willReadFrequently: true });
  if (!origCtx) throw new Error("Failed to acquire 2D context for original canvas");
  origCtx.drawImage(img, 0, 0);

  // 1024x1024 resized canvas for the model. Stretch-fit (matches
  // bg-remover-sandy reference). RMBG-1.4 was trained on stretched 1024x1024
  // input.
  const resized = document.createElement("canvas");
  resized.width = size;
  resized.height = size;
  const ctx = resized.getContext("2d", { willReadFrequently: true });
  if (!ctx) throw new Error("Failed to acquire 2D context for resized canvas");
  ctx.drawImage(img, 0, 0, size, size);
  return { resized, original };
}

function preprocessCanvasToTensor(
  canvas: HTMLCanvasElement,
  size: number,
  ortRuntime: OrtNamespace,
): OrtTensor {
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  if (!ctx) throw new Error("Failed to acquire 2D context for preprocess");
  const { data } = ctx.getImageData(0, 0, size, size);

  // Channel-first RRR…GGG…BBB…, normalize each channel by /255.0.
  // Layout matches bg-remover-sandy reference exactly.
  const tensorData = new Float32Array(1 * 3 * size * size);
  const plane = size * size;
  for (let i = 0; i < plane; i++) {
    const px = i * 4;
    tensorData[i] = data[px] / 255.0;
    tensorData[plane + i] = data[px + 1] / 255.0;
    tensorData[2 * plane + i] = data[px + 2] / 255.0;
  }
  return new ortRuntime.Tensor("float32", tensorData, [1, 3, size, size]);
}

function applyMaskToCanvas(
  original: HTMLCanvasElement,
  maskData: Float32Array | Uint8Array | Int8Array | number[] | ArrayLike<number>,
  modelSize: number,
): HTMLCanvasElement {
  // Mask is at modelSize × modelSize. Resize back to the original canvas
  // dimensions using a temporary mask canvas, then write to the alpha channel
  // of the original-sized image data.
  const maskCanvas = document.createElement("canvas");
  maskCanvas.width = modelSize;
  maskCanvas.height = modelSize;
  const maskCtx = maskCanvas.getContext("2d", { willReadFrequently: true });
  if (!maskCtx) throw new Error("Failed to acquire 2D context for mask");

  const maskImageData = maskCtx.createImageData(modelSize, modelSize);
  for (let i = 0; i < modelSize * modelSize; i++) {
    // Mask values from RMBG-1.4 are in [0,1]. Clamp + scale to [0,255].
    const raw = Number(maskData[i] ?? 0);
    const v = Math.max(0, Math.min(1, raw));
    const a = Math.round(v * 255);
    const px = i * 4;
    maskImageData.data[px] = a;
    maskImageData.data[px + 1] = a;
    maskImageData.data[px + 2] = a;
    maskImageData.data[px + 3] = 255;
  }
  maskCtx.putImageData(maskImageData, 0, 0);

  // Resize mask to original dimensions.
  const resizedMaskCanvas = document.createElement("canvas");
  resizedMaskCanvas.width = original.width;
  resizedMaskCanvas.height = original.height;
  const rctx = resizedMaskCanvas.getContext("2d", { willReadFrequently: true });
  if (!rctx) throw new Error("Failed to acquire 2D context for resized mask");
  rctx.imageSmoothingEnabled = true;
  rctx.imageSmoothingQuality = "high";
  rctx.drawImage(maskCanvas, 0, 0, original.width, original.height);

  // Composite: copy original RGB, replace alpha from resized mask R channel.
  const origCtx = original.getContext("2d", { willReadFrequently: true });
  if (!origCtx) throw new Error("Failed to acquire 2D context for composite");
  const origData = origCtx.getImageData(0, 0, original.width, original.height);
  const maskFull = rctx.getImageData(0, 0, original.width, original.height);
  for (let i = 0; i < origData.data.length; i += 4) {
    origData.data[i + 3] = maskFull.data[i]; // alpha = mask R
  }
  origCtx.putImageData(origData, 0, 0);
  return original;
}

// ---------- Main public entry point ----------

export async function processMobileImage(image: File): Promise<File> {
  if (!mobileState.session) {
    throw new Error("Mobile ORT session not initialized — call initializeMobileOrtModel() first");
  }

  const baseContext: Record<string, unknown> = {
    modelId: MODEL_ID_FOR_REPORTING,
    fileName: image.name,
    fileType: image.type,
    fileSize: image.size,
    path: "iOS-direct-ort",
  };

  const img = await withMobilePhase(
    "image decode",
    IMAGE_DECODE_TIMEOUT_MS,
    () => decodeImageElement(image),
    baseContext,
  );

  const imageContext: Record<string, unknown> = {
    ...baseContext,
    image: {
      fileName: image.name,
      fileType: image.type,
      fileSize: image.size,
      width: img.naturalWidth,
      height: img.naturalHeight,
    },
  };

  const { resized, original } = await withMobilePhase(
    "resize/preprocess",
    PREPROCESS_TIMEOUT_MS,
    async () => imageToInputCanvas(img, MODEL_INPUT_SIZE),
    imageContext,
  );

  const ortRuntime = mobileState.ort;
  if (!ortRuntime) {
    throw new Error("Mobile ORT runtime not initialized — call initializeMobileOrtModel() first");
  }

  const inputTensor = await withMobilePhase(
    "tensor creation",
    PREPROCESS_TIMEOUT_MS,
    async () => preprocessCanvasToTensor(resized, MODEL_INPUT_SIZE, ortRuntime),
    imageContext,
  );

  const session = mobileState.session!;
  const feeds: Record<string, OrtTensor> = { [mobileState.inputName]: inputTensor };

  const results = await withMobilePhase(
    "inference/session run",
    INFERENCE_TIMEOUT_MS,
    () => session.run(feeds),
    imageContext,
  );

  const maskData = await withMobilePhase(
    "mask/postprocess",
    POSTPROCESS_TIMEOUT_MS,
    async () => {
      const outputTensor = results[mobileState.outputName];
      if (!outputTensor) {
        throw new Error(
          `Model output "${mobileState.outputName}" not found in results (got: ${Object.keys(results).join(", ")})`,
        );
      }
      return outputTensor.data as Float32Array;
    },
    imageContext,
  );

  const compositedCanvas = await withMobilePhase(
    "mask/postprocess:apply",
    POSTPROCESS_TIMEOUT_MS,
    async () => applyMaskToCanvas(original, maskData, MODEL_INPUT_SIZE),
    imageContext,
  );

  const blob = await withMobilePhase(
    "canvas/blob/download creation",
    CANVAS_BLOB_TIMEOUT_MS,
    () =>
      new Promise<Blob>((resolve, reject) =>
        compositedCanvas.toBlob(
          (b) => (b ? resolve(b) : reject(new Error("canvas.toBlob returned null"))),
          "image/png",
        ),
      ),
    imageContext,
  );

  const [stem] = image.name.split(".");
  return new File([blob], `${stem}-nobg.png`, { type: "image/png" });
}

export function getMobileOrtModelInfo(): { modelId: string; modelBytes: number; ready: boolean } {
  return {
    modelId: MODEL_ID_FOR_REPORTING,
    modelBytes: mobileState.modelBytes,
    ready: mobileState.session !== null,
  };
}

// Test/diagnostic helper — lets the host force-reinitialize the session
// (e.g. between unit tests). Not used in production app flow.
export function disposeMobileOrtSession(): void {
  if (mobileState.session) {
    try {
      // onnxruntime-web's release is async but we don't await it here — the
      // session reference is dropped immediately so future calls re-init.
      void mobileState.session.release?.();
    } catch {
      // ignore
    }
  }
  mobileState.session = null;
  mobileState.ort = null;
  mobileState.modelBytes = 0;
}
